/**
 * @file Связь основного потока с worker одного сегмента.
 *
 * Проверяет сообщения и прогресс, следит за зависанием и завершает worker
 * при успехе, ошибке или отмене. Сам HTML worker не изменяет.
 */
import type { UploadPorts } from './engine.ts'
import { UploadError, isUploadErrorCode } from './model.ts'
import type { SegmentStatus } from './model.ts'
import type { SegmentWorkerRequest } from './segmentWorker'

export const segmentInWorker: UploadPorts['segment'] = (
    request,
    range,
    handle,
    signal,
    progress,
) => {
    signal.throwIfAborted()

    // Promise — результат всего сегмента. Отдельные сообщения worker обновляют
    // прогресс, но завершить Promise имеют право только finish() и ошибка запуска.
    return new Promise((resolve, reject) => {
        let worker: Worker

        try {
            worker = new Worker(
                new URL('./segmentWorker.ts', import.meta.url),
                { type: 'module' },
            )
        } catch {
            reject(new UploadError('worker'))

            return
        }

        // Все источники завершения сходятся в finish: ответ worker, его сбой,
        // таймер и пользовательская отмена. settled защищает от их совпадения.
        let timer: ReturnType<typeof setTimeout>
        let uploaded = 0
        let settled = false

        function finish(error?: unknown) {
            if (settled) return

            settled = true
            clearTimeout(timer)
            signal.removeEventListener('abort', abort)
            worker.terminate()
            if (error) reject(error)
            else resolve()
        }

        function abort() {
            finish(signal.reason)
        }

        // Таймер отслеживает отсутствие сообщений, а не общую длительность файла.
        // Каждое сообщение сдвигает предел ещё на 90 секунд.
        function watchdog() {
            clearTimeout(timer)
            timer = setTimeout(() => finish(new UploadError('worker')), 90_000)
        }

        // Сначала подключаем обработчики, затем отправляем задание: даже быстрый
        // ответ worker должен быть обработан.
        signal.addEventListener('abort', abort, { once: true })
        worker.onerror = (event) => {
            event.preventDefault()
            finish(new UploadError('worker'))
        }
        worker.onmessageerror = () => finish(new UploadError('worker'))

        worker.onmessage = (event) => {
            if (settled) return

            watchdog()
            const data = event.data

            // Код ошибки передаётся между потоками как строка, поэтому проверяем
            // его перед созданием нашего объекта UploadError.
            if (data?.type === 'error') {
                finish(
                    new UploadError(
                        isUploadErrorCode(data.code) ? data.code : 'worker',
                    ),
                )

                return
            }

            // Одного done недостаточно: worker должен был подтвердить весь диапазон.
            if (data?.type === 'done') {
                finish(
                    uploaded === range.end - range.start
                        ? undefined
                        : new UploadError('protocol'),
                )

                return
            }

            // Прогресс обязан расти в пределах сегмента. Сообщение с другим
            // форматом считаем ошибкой протокола, а не молча игнорируем.
            if (
                data?.type !== 'progress' ||
                !Number.isSafeInteger(data.uploaded) ||
                data.uploaded < uploaded ||
                data.uploaded > range.end - range.start ||
                !['uploading', 'retrying', 'done'].includes(data.status)
            ) {
                finish(new UploadError('protocol'))

                return
            }

            uploaded = data.uploaded
            progress(uploaded, data.status as SegmentStatus)
        }

        const message: SegmentWorkerRequest = { request, range, handle }
        watchdog()

        try {
            worker.postMessage(message)
        } catch {
            finish(new UploadError('worker'))
        }
    })
}
